import React from 'react';
import { toPersianDigits } from '../../utils/timeUtils.js';
import { useAuthStore } from '../../stores/authStore.js';

export type SyncStatusState = 'synced' | 'syncing' | 'offline' | 'error' | 'local';

export interface SyncStatusProps {
  state: SyncStatusState;
  queued?: number;
  queuedCount?: number;
  compact?: boolean;
  label?: string;
  onClick?: () => void;
  className?: string;
  'data-testid'?: string;
}

export const SyncStatus: React.FC<SyncStatusProps> = ({
  state: requestedState,
  queued,
  queuedCount,
  compact = false,
  label,
  onClick,
  className = '',
  'data-testid': testId
}) => {
  // FND-37: a guest session never syncs; say so instead of claiming "synced".
  const isGuest = useAuthStore((s) => s.isGuest);
  const state: SyncStatusState = isGuest && !label ? 'local' : requestedState;
  const count = queued ?? queuedCount ?? 0;
  const isInteractive = Boolean(onClick);

  const getDotClass = () => {
    switch (state) {
      case 'local':
        return 'bg-base-content/40';
      case 'synced':
        return 'bg-success';
      case 'syncing':
        return 'bg-info animate-pulse';
      case 'offline':
        return 'bg-warning';
      case 'error':
        return 'bg-error';
    }
  };

  const getLabel = () => {
    if (label) return label;
    switch (state) {
      case 'local':
        return 'فقط روی این دستگاه';
      case 'synced':
        return 'همگام · همین حالا';
      case 'syncing':
        return 'در حال همگام‌سازی…';
      case 'offline':
        return count > 0 ? `در صف (${toPersianDigits(count)})` : 'آفلاین';
      case 'error':
        return 'خطا در همگام‌سازی';
    }
  };

  const getContainerClass = () => {
    switch (state) {
      case 'offline':
        return 'bg-warning/10 border-warning/35 text-warning-soft';
      case 'error':
        return 'bg-error/10 border-error/35 text-error-soft';
      case 'syncing':
      case 'synced':
      default:
        return 'bg-base-100 border-base-300 text-base-content/80';
    }
  };

  if (compact) {
    const Component = isInteractive ? 'button' : 'div';
    return (
      <Component
        type={isInteractive ? 'button' : undefined}
        onClick={onClick}
        title={getLabel()}
        aria-label={getLabel()}
        data-testid={testId || 'sync-status-compact'}
        className={`relative inline-flex items-center justify-center min-w-[44px] min-h-[44px] p-3 -m-3 shrink-0 rounded-full bg-transparent border-0 transition ${
          isInteractive ? 'cursor-pointer active:scale-95 focus-visible:outline-none focus-visible:ring-2 ring-primary' : 'cursor-default'
        } ${className}`}
      >
        <span className={`size-2 rounded-full shrink-0 ${getDotClass()}`} aria-hidden="true" />
      </Component>
    );
  }

  const Component = isInteractive ? 'button' : 'div';
  return (
    <Component
      type={isInteractive ? 'button' : undefined}
      onClick={onClick}
      data-testid={testId || 'sync-status-pill'}
      className={`inline-flex items-center gap-2 h-9 px-3 rounded-xl border text-xs font-medium select-none transition ${
        isInteractive ? 'cursor-pointer hover:border-base-content/30 active:scale-[0.98]' : 'cursor-default'
      } ${getContainerClass()} ${className}`}
    >
      <span className={`size-2 rounded-full shrink-0 ${getDotClass()}`} aria-hidden="true" />
      <span>{getLabel()}</span>
    </Component>
  );
};

SyncStatus.displayName = 'SyncStatus';
